import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Plus, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAvas } from '../../hooks/useDashboardData';
import { AvALightbox } from '../chat/AvALightbox';

// Slugify an AvA name for shareable URLs (e.g. "Maya" → "maya").
const avaSlug = (name) => (name || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// Map old companion personality types to new practice styles
const STYLE_MAP = {
  'Friend': 'Encouraging', 'friend': 'Encouraging',
  'Therapist': 'Reflective', 'therapist': 'Reflective',
  'Coach': 'Challenging', 'coach': 'Challenging',
  'Mentor': 'Steady', 'mentor': 'Steady',
  'Companion': 'Energising', 'companion': 'Energising',
};
const mapToPracticeStyle = (type) => STYLE_MAP[type] || type;

// Aug 2026 — specialization grouping + functional status badges
const PRESET_TOPICS = {
  maya_default: { cat: 'guide', label: 'Guide', color: '#F5831F' },
  rebecca_default: { cat: 'stem', label: 'STEM', color: '#2EAA5C' },
  neo_default: { cat: 'social', label: 'Social', color: '#7B4FD8' },
  specialist_chloe: { cat: 'social', label: 'Social', color: '#059669' },
  specialist_leo: { cat: 'relationships', label: 'Relationships', color: '#E11D48' },
  specialist_marcus: { cat: 'career', label: 'Career', color: '#1D4ED8' },
  specialist_ethan: { cat: 'career', label: 'Speaking', color: '#B45309' },
  specialist_sarah: { cat: 'life', label: 'Organization', color: '#0D9488' },
  specialist_petra: { cat: 'life', label: 'Online Life', color: '#DB2777' },
};

const TOPIC_TABS = [
  { id: 'all', label: 'All' },
  { id: 'social', label: 'Social' },
  { id: 'relationships', label: 'Relationships' },
  { id: 'career', label: 'Career & Speaking' },
  { id: 'life', label: 'Life & Online' },
  { id: 'stem', label: 'STEM' },
];

const MyAvAsCard = () => {
  const { user } = useAuth();
  const { avas, loading, error, createAva } = useAvas();
  const [creating, setCreating] = useState(false);
  const [lightboxAva, setLightboxAva] = useState(null);
  const [topicTab, setTopicTab] = useState('all');
  const navigate = useNavigate();
  const isTeen = user?.age_band === 'teen';

  const visibleAvas = topicTab === 'all'
    ? avas
    : avas.filter(a => {
        const t = PRESET_TOPICS[a.avatar_config?.preset];
        return t ? t.cat === topicTab || t.cat === 'guide' : false;
      });

  // Check if user can create more AvAs based on subscription
  const canCreateNewAvA = () => {
    const tierLimits = {
      free: 1,
      ava_prime: 2,
      ava_companion: 5,
      ava_elite: 999
    };
    const limit = tierLimits[user?.subscription_tier] || 1;
    return avas.length < limit;
  };

  const handleCreateAvA = async () => {
    setCreating(true);
    try {
      // Create a default AVA - in a real app, this would open a modal
      await createAva({
        name: `AvA ${avas.length + 1}`,
        personality_type: 'Encouraging',
        ava_role: 'friend',
        is_primary: avas.length === 0,
      });
    } catch (err) {
      console.error('Failed to create AVA:', err);
    } finally {
      setCreating(false);
    }
  };

  const formatLastActive = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div className="card" data-testid="my-avas-card">
        <div className="card-header">
          <h2 className="card-title">My Trainers</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" data-testid="my-avas-card">
        <div className="card-header">
          <h2 className="card-title">My Trainers</h2>
        </div>
        <p className="text-sm text-red-500 py-4">Failed to load AVAs</p>
      </div>
    );
  }

  return (
    <div className="card" data-testid="my-avas-card">
      <div className="card-header">
        <h2 className="card-title">My Trainers</h2>
        <a href="/conversations" className="card-action">View All</a>
      </div>

      {/* Specialization tabs — no more scanning 9 avatars for the right tutor */}
      <div className="flex gap-1.5 flex-wrap mb-3" data-testid="trainer-topic-tabs">
        {TOPIC_TABS.map(t => (
          <button key={t.id} onClick={() => setTopicTab(t.id)} data-testid={`trainer-tab-${t.id}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${topicTab === t.id ? 'text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`}
            style={topicTab === t.id ? { backgroundColor: '#F5831F' } : {}}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {visibleAvas.length === 0 ? (
          <p className="text-sm text-gray-600 py-2">
            Your AvA tutor team is on its way — check back in a moment!
          </p>
        ) : (
          visibleAvas.map((ava) => (
            <div
              key={ava.ava_id}
              className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid={`ava-item-${ava.ava_id}`}
            >
              <div className="flex items-center space-x-3 flex-1">
                <button
                  type="button"
                  onClick={() => setLightboxAva(ava)}
                  className="rounded-full focus:outline-none focus:ring-2 focus:ring-purple-400 cursor-zoom-in"
                  aria-label={`Enlarge ${ava.name} animation`}
                  data-testid={`ava-thumb-${ava.ava_id}`}
                >
                  <img src={ava.avatar_url} alt={ava.name} className="w-12 h-12 rounded-full object-cover" />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900">{ava.name}</p>
                    {ava.is_primary && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">
                        Primary
                      </span>
                    )}
                    {ava.is_fixed && (
                      <span
                        className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full"
                        style={{ background: '#DBEAFE', color: '#1E40AF' }}
                        title="Anchor trainer — always available, identity managed by the admin"
                        data-testid={`ava-fixed-badge-${ava.ava_id}`}
                      >
                        Fixed
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    {(() => {
                      const topic = PRESET_TOPICS[ava.avatar_config?.preset];
                      return topic ? (
                        <span className="px-2 py-0.5 text-[10px] font-bold rounded-full"
                          style={{ backgroundColor: `${topic.color}15`, color: topic.color }}
                          data-testid={`ava-topic-${ava.ava_id}`}>
                          {topic.label}
                        </span>
                      ) : null;
                    })()}
                    <span className="px-2 py-0.5 text-[10px] font-bold rounded-full"
                      style={{ backgroundColor: '#DCFCE7', color: '#15803D' }}
                      data-testid={`ava-ready-${ava.ava_id}`}>
                      Ready to Practise
                    </span>
                    {(ava.avatar_config?.preset || '').startsWith('specialist_') && (
                      <span className="px-2 py-0.5 text-[10px] font-bold rounded-full"
                        style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                        Topic-locked
                      </span>
                    )}
                    {ava.conversation_count > 0 && (
                      <span className="text-[10px] text-gray-400">{ava.conversation_count} chats</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  const slug = avaSlug(ava.name);
                  navigate(slug ? `/conversations/practice/${slug}` : '/conversations/chat', { state: { selectedAva: ava } });
                }}
                className="btn btn-primary btn-small"
                data-testid={`start-chat-${ava.ava_id}`}
              >
                <MessageCircle size={14} />
                <span>Chat</span>
              </button>
            </div>
          ))
        )}

        {/* Create New AvA Button (never for teens — fixed 9-tutor library) */}
        {!isTeen && canCreateNewAvA() && (
          <button
            onClick={handleCreateAvA}
            disabled={creating}
            className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="create-new-ava-button"
          >
            {creating ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Plus size={18} />
            )}
            <span className="font-medium">
              {creating ? 'Creating...' : 'Create New AvA'}
            </span>
          </button>
        )}
      </div>

      {/* Tap-to-enlarge AvA animation lightbox */}
      <AvALightbox
        open={!!lightboxAva}
        onClose={() => setLightboxAva(null)}
        name={lightboxAva?.name}
        avatarUrl={lightboxAva?.avatar_url}
        avaId={lightboxAva?.ava_id}
      />
    </div>
  );
};

export default MyAvAsCard;
